import { cn, hasValue } from '@woonstad/helpers-utils';
import { TextareaHTMLAttributes, ReactElement } from 'react';

import { sharedInputClasses } from '../../form/shared-input';
import { labelRegularClasses, Typography } from '../../typography/Typography';
import { useFormField } from '../FormField';

type Props = {
  maxCharacters?: number;
  transformValueOnChange?: (value: string) => string;
  // used when another translation is needed for example in Stadswonen Portal
  characterCountComponent?: (maxCharacters: number, charsLeft: number) => ReactElement;
}

export const Textarea = (props: TextareaHTMLAttributes<HTMLTextAreaElement> & Props) => {
  const {
    maxCharacters,
    transformValueOnChange,
    characterCountComponent,
    ...elementProps
  } = props;

  const {
    field,
    fieldState,
    formItemId,
  } = useFormField();

  const classes = cn(
    sharedInputClasses,
    labelRegularClasses,
    'flex items-center justify-between gap-3 text-input-activated placeholder:text-input-enabled',
    field?.disabled && 'border-none bg-input-disabled text-input-disabled',
    fieldState.error && 'border-input-error',
  );

  const guardedMaxCharacters = props.maxCharacters ?? Infinity;
  const charsLeft = field?.value?.length ? guardedMaxCharacters - field.value.length : guardedMaxCharacters;

  return (
    <>
      <textarea
        {...elementProps}
        className={cn('h-40 w-full resize-none', classes)}
        disabled={field?.disabled}
        name={field?.name}
        value={field?.value}
        onBlur={field?.onBlur}
        onChange={(ev) => {
          const { value } = ev.currentTarget;

          const transformedValue = hasValue(transformValueOnChange) ? transformValueOnChange(value) : value;

          field?.onChange(transformedValue);
        }}
        id={formItemId}
      />
      {props.maxCharacters && characterCountComponent?.(
        props.maxCharacters, charsLeft
      )}

      {!characterCountComponent && props.maxCharacters && (
        <CharacterCount
          id={props.id}
          currentCharsAmount={field?.value?.length ?? 0}
          maxCharacters={props.maxCharacters}
        />
      )}
    </>
  );
};

interface CharacterCountProps { id?: string; maxCharacters: number; currentCharsAmount: number }

function CharacterCount(props: CharacterCountProps) {
  const charsLeft = props.maxCharacters - props.currentCharsAmount;

  return (
    <Typography
      variant="label-small"
      htmlFor={props.id}
      className="mt-2 text-input-hint"
      type="div"
    >
      Maximaal {props.maxCharacters} karakters{' ('}
      <span
        className={cn(
          (charsLeft <= 10) ? 'text-error' : undefined,
        )}
      >
        {charsLeft >= 0 ? (
          `nog ${charsLeft}`
        ) : (
          `${Math.abs(charsLeft)} teveel`
        )}
      </span>
      {')'}
    </Typography>
  );
}

Textarea.displayName = 'Textarea';
